import React from 'react';
import { FiChevronRight, FiInfo } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useComplianceCentre } from '../context/ComplianceCentreContext';
import { actionRow, activityRow } from '../complianceCentreUi';

export default function DashboardActivitySection() {
  const { data, setPanel, emptyMessage } = useComplianceCentre();
  const openActionsData = (data?.actions || []).slice(0, 5).map(actionRow);
  const recentActivityData = (data?.activity || []).slice(0, 5).map(activityRow);
  return (
    <div className="w-full bg-slate-50/30 font-sans select-none space-y-4">
      
      {/* Dual Column Container Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        
        {/* LEFT CARD PANEL: Open Actions Grid Table */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 flex flex-col justify-between min-h-[380px]">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-slate-900 font-extrabold text-base tracking-tight">Open Actions</h3>
              <button type="button" onClick={() => setPanel("actions")} className="text-xs font-bold text-blue-600 hover:text-blue-700 transition flex items-center space-x-0.5">
                <span>View all actions</span>
                <FiChevronRight className="w-3.5 h-3.5 mt-0.5" />
              </button>
            </div>

            {/* Overflow wrapper to protect mobile viewports */}
            <div className="overflow-x-auto w-full">
              <div className="min-w-[540px]">
                {/* Table Headers */}
                <div className="grid grid-cols-12 gap-2 text-[10px] font-black text-gray-400 uppercase tracking-wider pb-2 border-b border-gray-50 text-left">
                  <div className="col-span-5">Action</div>
                  <div className="col-span-3">Area</div>
                  <div className="col-span-1.5 text-center">Priority</div>
                  <div className="col-span-1.5 pl-2">Due Date</div>
                  <div className="col-span-1">Assigned To</div>
                </div>

                {/* Table Dynamic Content Rows */}
                <div className="divide-y divide-gray-50">
                  {!openActionsData.length && <p className="py-5 text-xs text-slate-400">{emptyMessage('No open actions recorded.')}</p>}
                  {openActionsData.map((row) => (
                    <Link to={row.path} key={row.id} className="grid grid-cols-12 gap-2 py-3 items-center text-xs text-left group hover:bg-slate-50/40 rounded-lg px-0.5 transition duration-150">
                      <div className="col-span-5 font-bold text-slate-800 pr-2 line-clamp-1">{row.action}</div>
                      <div className="col-span-3 font-semibold text-slate-400 line-clamp-1">{row.area}</div>
                      <div className="col-span-1.5 flex justify-center">
                        <span className={`text-[10px] font-black px-2 py-0.5 border rounded-md min-w-[54px] text-center ${row.priorityStyle}`}>
                          {row.priority}
                        </span>
                      </div>
                      <div className="col-span-1.5 font-bold text-slate-700 pl-2 whitespace-nowrap">{row.dueDate}</div>
                      <div className="col-span-1 font-semibold text-slate-400 truncate">{row.assignedTo}</div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <button type="button" onClick={() => setPanel("actions")} className="w-full text-xs font-bold text-blue-600 hover:text-blue-700 transition border border-gray-100 bg-white shadow-sm py-2 rounded-xl mt-4 flex items-center justify-center space-x-1">
            <span>View all open actions</span>
            <FiChevronRight className="w-3.5 h-3.5 mt-0.5" />
          </button>
        </div>

        {/* RIGHT CARD PANEL: Recent Activity Stream */}
        <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 flex flex-col justify-between min-h-[380px]">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-slate-900 font-extrabold text-base tracking-tight">Recent Activity</h3>
              <button type="button" onClick={() => setPanel("activity")} className="text-xs font-bold text-blue-600 hover:text-blue-700 transition flex items-center space-x-0.5">
                <span>View all activity</span>
                <FiChevronRight className="w-3.5 h-3.5 mt-0.5" />
              </button>
            </div>

            {/* List Array Map Stream */}
            <div className="divide-y divide-gray-50">
              {!recentActivityData.length && <p className="py-5 text-xs text-slate-400">{emptyMessage('No saved activity available.')}</p>}
              {recentActivityData.map((activity) => {
                const ActivityIcon = activity.icon;
                return (
                  <Link to={activity.path} key={activity.id} className="py-2.5 flex items-center justify-between space-x-4 group hover:bg-slate-50/40 px-1 rounded-xl transition duration-150">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className={`w-9 h-9 ${activity.iconStyle} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <ActivityIcon className="w-4 h-4" />
                      </div>
                      <div className="text-left min-w-0 space-y-0.5">
                        <h4 className="text-xs font-extrabold text-slate-900 truncate">{activity.title}</h4>
                        <p className="text-[10px] font-semibold text-slate-400 truncate">{activity.subtitle}</p>
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0 space-y-0.5">
                      <span className="block text-[10px] font-bold text-slate-700 whitespace-nowrap">{activity.date}</span>
                      <span className="block text-[10px] font-semibold text-slate-400 truncate">{activity.author}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          <button type="button" onClick={() => setPanel("activity")} className="w-full text-xs font-bold text-blue-600 hover:text-blue-700 transition border border-gray-100 bg-white shadow-sm py-2 rounded-xl mt-4 flex items-center justify-center space-x-1">
            <span>View all activity</span>
            <FiChevronRight className="w-3.5 h-3.5 mt-0.5" />
          </button>
        </div>

      </div>

      {/* BOTTOM LEGAL DISCLOSURE BAR */}
      <div className="w-full border border-blue-100 bg-blue-50/30 p-3 rounded-xl flex items-center space-x-2 text-[11px] font-semibold text-blue-800/80">
        <FiInfo className="w-4 h-4 text-blue-600 flex-shrink-0" />
        <p className="text-left">
          Compliance records are based on saved checks, incidents, equipment and supporting files.
        </p>
      </div>

    </div>
  );
}
